// Pattern Evaluator Utility
// Evaluates pattern templates by replacing placeholders with actual values

/**
 * Evaluates a pattern template by replacing placeholders
 * @param {string} pattern - The pattern template with {{placeholders}}
 * @param {object} context - Context object containing data (message, audiences, topics, etc.)
 * @returns {string} - Evaluated pattern with replaced values
 */
export function evaluatePattern(pattern, context) {
  if (!pattern) return '';

  let result = pattern;

  // First check if the entire pattern is a conditional expression
  // Format: {{variable}}=value?true_result:false_result
  const conditionalMatch = pattern.match(/^\{\{([^}]+)\}\}=([^?]+)\?([^:]*):(.*)$/);
  if (conditionalMatch) {
    const [, varExpression, compareValue, trueResult, falseResult] = conditionalMatch;
    const varValue = evaluateExpression(varExpression.trim(), context);
    return varValue === compareValue.trim() ? trueResult : falseResult;
  }

  // Replace all {{placeholder}} patterns
  const regex = /\{\{([^}]+)\}\}/g;
  result = result.replace(regex, (match, expression) => {
    try {
      return evaluateExpression(expression.trim(), context);
    } catch (error) {
      console.warn(`Failed to evaluate expression: ${expression}`, error);
      return match; // Return original if evaluation fails
    }
  });

  return result;
}

/**
 * Apply a modifier function to a value
 * Supported modifiers:
 *   - noext: Remove file extension (e.g., "video.mp4" -> "video")
 *   - upper: Convert to uppercase
 *   - lower: Convert to lowercase
 *   - trim: Trim whitespace
 * @param {string} value - The value to modify
 * @param {string} modifier - The modifier name
 * @returns {string} - Modified value
 */
function applyModifier(value, modifier) {
  if (!value) return value;

  switch (modifier.toLowerCase()) {
    case 'noext':
      // Remove file extension (last . and everything after)
      const lastDot = value.lastIndexOf('.');
      return lastDot > 0 ? value.substring(0, lastDot) : value;
    case 'upper':
      return value.toUpperCase();
    case 'lower':
      return value.toLowerCase();
    case 'trim':
      return value.trim();
    default:
      return value;
  }
}

/**
 * Evaluates a single expression
 * @param {string} expression - The expression to evaluate (e.g., "Audience_Key" or "audiences[Audience_Key].Strategy")
 *                              Supports pipe modifiers: "Video1|noext" removes file extension
 * @param {object} context - Context object containing data
 * @returns {string} - Evaluated value
 */
function evaluateExpression(expression, context) {
  // Check for pipe modifiers (e.g., "Video1|noext|upper")
  const parts = expression.split('|');
  const baseExpression = parts[0].trim();
  const modifiers = parts.slice(1).map(m => m.trim());

  // Evaluate the base expression
  let value = evaluateBaseExpression(baseExpression, context);

  // Apply modifiers in order
  for (const modifier of modifiers) {
    value = applyModifier(value, modifier);
  }

  return value;
}

/**
 * Evaluates the base expression without modifiers
 * @param {string} expression - The expression to evaluate
 * @param {object} context - Context object containing data
 * @returns {string} - Evaluated value
 */
function evaluateBaseExpression(expression, context) {
  // Handle simple variables first - try exact match first, then case-insensitive
  if (context.hasOwnProperty(expression)) {
    return String(context[expression] || '');
  }

  // Try case-insensitive match
  const expressionLower = expression.toLowerCase();
  for (const key in context) {
    if (key.toLowerCase() === expressionLower) {
      return String(context[key] || '');
    }
  }

  // Handle array access patterns like audiences[key].field
  const arrayAccessMatch = expression.match(/^(\w+)\[([^\]]+)\]\.(\w+)$/);
  if (arrayAccessMatch) {
    const [, arrayName, keyExpr, field] = arrayAccessMatch;

    // Evaluate the key expression first
    const key = evaluateExpression(keyExpr, context);

    // Get the array from context
    const array = context[arrayName];
    if (!array || !Array.isArray(array)) {
      return '';
    }

    // Find the item by key field
    const item = array.find(item => item.key === key);
    if (!item) {
      return '';
    }

    // Return the field value (case-insensitive lookup)
    // Try exact match first, then case-insensitive
    if (item[field] !== undefined) {
      return String(item[field] || '');
    }

    // Try lowercase version
    const fieldLower = field.toLowerCase();
    if (item[fieldLower] !== undefined) {
      return String(item[fieldLower] || '');
    }

    // Try with underscores (PascalCase to snake_case)
    const fieldSnake = field.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
    if (item[fieldSnake] !== undefined) {
      return String(item[fieldSnake] || '');
    }

    return '';
  }

  // Handle nested object access like message.field
  const objectAccessMatch = expression.match(/^(\w+)\.(\w+)$/);
  if (objectAccessMatch) {
    const [, objectName, field] = objectAccessMatch;
    const obj = context[objectName];
    if (obj && typeof obj === 'object') {
      // Try exact match first
      if (obj[field] !== undefined) {
        return String(obj[field] || '');
      }

      // Try lowercase version
      const fieldLower = field.toLowerCase();
      if (obj[fieldLower] !== undefined) {
        return String(obj[fieldLower] || '');
      }

      // Try snake_case version (PascalCase to snake_case)
      const fieldSnake = field.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
      if (obj[fieldSnake] !== undefined) {
        return String(obj[fieldSnake] || '');
      }

      return '';
    }
  }

  return '';
}

/**
 * Build trafficking URL with smart query parameter handling
 * @param {string} landingUrl - Base landing URL
 * @param {object} params - Query parameters object
 * @returns {string} - Complete URL with query parameters
 */
export function buildTraffickingUrl(landingUrl, params) {
  if (!landingUrl) return '';

  // Remove trailing & if present
  let url = landingUrl.trim().replace(/&$/, '');

  // Check if URL already has query parameters
  const hasQueryParams = url.includes('?');
  const separator = hasQueryParams ? '&' : '?';

  // Build query string from params
  const queryParts = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });

  if (queryParts.length === 0) {
    return url;
  }

  return `${url}${separator}${queryParts.join('&')}`;
}

/**
 * Generate PMMID based on pattern
 * @param {object} message - Message object
 * @param {array} audiences - Array of audiences
 * @param {string} pattern - PMMID pattern template
 * @returns {string} - Generated PMMID
 */
export function generatePMMID(message, audiences, pattern) {
  const context = {
    ...message,
    audiences,
    Audience_Key: message.audience,
    Topic_Key: message.topic,
    Number: message.number,
    Variant: message.variant,
    Version: message.version
  };

  return evaluatePattern(pattern, context);
}

/**
 * Generate Topic Key based on pattern
 * @param {object} topic - Topic object
 * @param {string} pattern - Topic key pattern template
 * @returns {string} - Generated topic key
 */
export function generateTopicKey(topic, pattern) {
  const context = {
    ...topic,
    Tag1: topic.tag1 || '',
    Tag2: topic.tag2 || '',
    Tag3: topic.tag3 || '',
    Tag4: topic.tag4 || '',
    Product: topic.product || ''
  };

  return evaluatePattern(pattern, context);
}

/**
 * Generate all trafficking fields based on patterns
 * @param {object} message - Message object
 * @param {array} audiences - Array of audiences
 * @param {object} patterns - Trafficking patterns from settings
 * @returns {object} - Object with all trafficking fields
 */
export function generateTraffickingFields(message, audiences, patterns) {
  const context = {
    ...message,
    audiences,
    Audience_Key: message.audience,
    Topic_Key: message.topic,
    Number: message.number,
    Variant: message.variant,
    Version: message.version,
    Landing_URL: message.landingUrl || '',
    PMMID: message.pmmid || ''
  };

  // Evaluate all trafficking patterns
  const utmCampaign = evaluatePattern(patterns.utm_campaign, context);
  const utmSource = evaluatePattern(patterns.utm_source, context);
  const utmMedium = evaluatePattern(patterns.utm_medium, context);
  const utmContent = evaluatePattern(patterns.utm_content, context);
  const utmTerm = evaluatePattern(patterns.utm_term, context);
  const utmCd26 = evaluatePattern(patterns.utm_cd26, context);

  // Build final URL
  const finalUrl = buildTraffickingUrl(message.landingUrl || '', {
    utm_campaign: utmCampaign,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_content: utmContent,
    utm_term: utmTerm,
    utm_cd26: utmCd26
  });

  return {
    utm_campaign: utmCampaign,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_content: utmContent,
    utm_term: utmTerm,
    utm_cd26: utmCd26,
    final_trafficked_url: finalUrl
  };
}
